import React from 'react';
import styled from 'styled-components';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaIdCard,
  FaEdit,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const ProfileHeader = styled.div`
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  color: white;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ProfileAvatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1rem;
  box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
`;

const ProfileName = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const ProfileRole = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

const ProfileContent = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #141340;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #F68B21;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateX(4px);
  }
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-weight: 600;
  color: #141340;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(246, 139, 33, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
    }
  }}
`;

const HRProfile: React.FC = () => {
  const { user } = useAuth();

  // Mock profile data
  const profileData = {
    name: 'Agnes Mwaseba',
    email: 'hradmin@example.com',
    phone: '+255 123 456 789',
    address: 'Dar es Salaam, Tanzania',
    employeeId: 'EMP-008',
    department: 'Human Resources',
    position: 'HR (Admin)',
    joinDate: '2022-03-15',
    supervisor: 'Managing Director',
    emergencyContact: '+255 987 654 321',
    emergencyName: 'John Mwaseba'
  };

  return (
    <Container>
      <PageTitle>
        <IconWrapper icon={FaUser} />
        My Profile
      </PageTitle>

      <ProfileCard>
        <ProfileHeader>
          <ProfileAvatar>
            {profileData.name.charAt(0)}
          </ProfileAvatar>
          <ProfileName>{profileData.name}</ProfileName>
          <ProfileRole>{profileData.position}</ProfileRole>
        </ProfileHeader>

        <ProfileContent>
          <Section>
            <SectionTitle>
              <IconWrapper icon={FaUser} />
              Personal Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaEnvelope} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Email Address</InfoLabel>
                  <InfoValue>{profileData.email}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaPhone} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Phone Number</InfoLabel>
                  <InfoValue>{profileData.phone}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaMapMarkerAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Address</InfoLabel>
                  <InfoValue>{profileData.address}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaIdCard} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Employee ID</InfoLabel>
                  <InfoValue>{profileData.employeeId}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaUser} />
              Employment Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Department</InfoLabel>
                  <InfoValue>{profileData.department}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Position</InfoLabel>
                  <InfoValue>{profileData.position}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaCalendarAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Join Date</InfoLabel>
                  <InfoValue>{new Date(profileData.joinDate).toLocaleDateString()}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Supervisor</InfoLabel>
                  <InfoValue>{profileData.supervisor}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaPhone} />
              Emergency Contact
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Emergency Contact Name</InfoLabel>
                  <InfoValue>{profileData.emergencyName}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaPhone} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Emergency Contact Phone</InfoLabel>
                  <InfoValue>{profileData.emergencyContact}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <ActionButtons>
            <Button variant="secondary">
              <IconWrapper icon={FaTimes} />
              Cancel
            </Button>
            <Button variant="primary">
              <IconWrapper icon={FaEdit} />
              Edit Profile
            </Button>
          </ActionButtons>
        </ProfileContent>
      </ProfileCard>
    </Container>
  );
};

export default HRProfile; 